import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Security as SecurityIcon,
  Extension as ExtensionIcon,
  History as HistoryIcon,
  BarChart as BarChartIcon,
  AdminPanelSettings as AdminIcon,
  Contacts as ContactsIcon
} from '@mui/icons-material';
import Logo from './Logo';

const Sidebar = () => {
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: DashboardIcon,
      description: 'Overview and analytics'
    },
    {
      name: 'Analytics',
      href: '/analytics',
      icon: BarChartIcon,
      description: 'Call analytics and insights'
    },
    {
      name: 'AI Agents',
      href: '/agents',
      icon: PeopleIcon,
      description: 'Manage your voice agents'
    },
    {
      name: 'Call History',
      href: '/calls',
      icon: HistoryIcon,
      description: 'View call logs and recordings'
    },
    {
      name: 'Contacts',
      href: '/contacts',
      icon: ContactsIcon,
      description: 'Manage your contacts and CRM'
    },
    {
      name: 'Phone Numbers',
      href: '/phone-numbers',
      icon: PhoneIcon,
      description: 'Manage your phone numbers'
    },
    {
      name: 'API Keys',
      href: '/api-keys',
      icon: SecurityIcon,
      description: 'Manage your API keys'
    },
    {
      name: 'Integrations',
      href: '/integrations',
      icon: ExtensionIcon,
      description: 'Manage third-party integrations'
    }
  ];

  // Add admin navigation if user is admin
  const adminNavigation = currentUser?.role === 'admin' ? [
    {
      name: 'Admin Panel',
      href: '/admin',
      icon: AdminIcon,
      description: 'Manage users and invitations'
    }
  ] : [];

  const allNavigation = [...navigation, ...adminNavigation];

  return (
    <Box
      sx={{
        width: 280,
        height: '100vh',
        position: 'fixed',
        left: 0,
        top: 0,
        backgroundColor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
        zIndex: 1200,
        display: { xs: 'none', lg: 'flex' },
        flexDirection: 'column'
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Logo showText={true} variant="default" size="small" />
        </Box>
      </Box>

      {/* Navigation - Takes up available space */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <List sx={{ px: 2, py: 1 }}>
          {allNavigation.map((item) => {
            const Icon = item.icon;
            const isActiveRoute = isActive(item.href);
            return (
              <ListItem key={item.name} disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  component={Link}
                  to={item.href}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: isActiveRoute ? 'primary.main' : 'transparent',
                    color: isActiveRoute ? 'primary.contrastText' : 'text.primary',
                    minHeight: 48,
                    px: 2,
                    '&:hover': {
                      backgroundColor: isActiveRoute ? 'primary.dark' : 'action.hover'
                    }
                  }}
                  onClick={() => isMobile && window.close()}
                >
                  <ListItemIcon sx={{ 
                    color: isActiveRoute ? 'primary.contrastText' : 'inherit',
                    minWidth: 48
                  }}>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    secondary={item.description}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      color: isActiveRoute ? 'primary.contrastText' : 'text.secondary'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
        
        <Divider sx={{ mx: 2, my: 2 }} />
      </Box>

      {/* User Info and Profile Configuration - Fixed at bottom */}
      {currentUser && (
        <Box sx={{ 
          p: 2, 
          borderTop: 1, 
          borderColor: 'divider',
          flexShrink: 0,
          backgroundColor: 'background.paper'
        }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="medium" noWrap>
              {currentUser.firstName} {currentUser.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {currentUser.email}
            </Typography>
          </Box>
          
          <List sx={{ py: 0 }}>
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                component={Link}
                to="/profile"
                sx={{
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 48 }}>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Profile Settings" 
                  secondary="Manage your account"
                />
              </ListItemButton>
            </ListItem>
            
            {currentUser.isOrganizationAdmin && (
              <ListItem disablePadding sx={{ mb: 1 }}>
                <ListItemButton
                  component={Link}
                  to="/organization/white-label"
                  sx={{
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 48 }}>
                    <SettingsIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Organization" 
                    secondary="White label settings"
                  />
                </ListItemButton>
              </ListItem>
            )}
            
            {/* Logout - Last item at the bottom */}
            <ListItem disablePadding>
              <ListItemButton
                onClick={handleLogout}
                sx={{
                  borderRadius: 2,
                  '&:hover': {
                    backgroundColor: 'action.hover'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 48 }}>
                  <LogoutIcon />
                </ListItemIcon>
                <ListItemText 
                  primary="Logout" 
                  secondary="Sign out of your account"
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      )}
    </Box>
  );
};

export default Sidebar;
